import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Heart, Sparkles, User, Users } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function Onboarding() {
  const { lang } = useLocale();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [gender, setGender] = useState("");

  const handleSubmit = async () => {
    if (!gender) {
      toast.error("Please select your gender");
      return;
    }

    setLoading(true);

    try {
      const response = await api.put("/users/gender", { gender });
      updateUser(response.data);
      if (lang !== "en") api.put("/users/profile", { language: lang }).catch(() => {});
      toast.success("Welcome! You received 50 free credits to get started!");
      navigate("/discover");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Heart className="w-10 h-10 text-primary fill-primary" />
          <span className="text-3xl font-bold font-['Outfit'] tracking-tight">LuxeMeet</span>
        </div>

        {/* Welcome message */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight mb-2">
            Welcome, {user?.name || "there"}!
          </h1>
          <p className="text-muted-foreground">
            One more step to complete your profile
          </p>
        </div>

        {/* Gender selection */}
        <div className="glass-card p-8 mb-6">
          <Label className="text-lg font-semibold mb-4 block">I am a</Label>
          
          <RadioGroup
            value={gender}
            onValueChange={setGender}
            className="space-y-4"
          >
            <div>
              <RadioGroupItem
                value="male"
                id="male"
                className="peer sr-only"
              />
              <Label
                htmlFor="male"
                className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/10 transition-all hover:bg-white/10"
                data-testid="gender-male-option"
              >
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">Man</p>
                  <p className="text-sm text-muted-foreground">
                    50 starter credits + purchase more to connect with women
                  </p>
                </div>
              </Label>
            </div>

            <div>
              <RadioGroupItem
                value="female"
                id="female"
                className="peer sr-only"
              />
              <Label
                htmlFor="female"
                className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 cursor-pointer peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/10 transition-all hover:bg-white/10"
                data-testid="gender-female-option"
              >
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">Woman</p>
                  <p className="text-sm text-muted-foreground">
                    Free access + 50 starter credits
                  </p>
                </div>
              </Label>
            </div>
          </RadioGroup>

          {gender && (
            <div className="mt-6 p-4 rounded-xl bg-secondary/10 border border-secondary/20">
              <div className="flex items-center gap-2 text-secondary mb-1">
                <Sparkles className="w-4 h-4" />
                <span className="font-semibold text-sm">Special Welcome Bonus</span>
              </div>
              <p className="text-sm text-muted-foreground">
                You'll receive 50 free credits to get started!
              </p>
            </div>
          )}
        </div>

        <Button
          onClick={handleSubmit}
          className="w-full h-12 bg-primary hover:bg-primary/90 rounded-xl font-semibold"
          disabled={loading || !gender}
          data-testid="complete-onboarding-btn"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
          ) : (
            "Complete Profile"
          )}
        </Button>
      </div>
    </div>
  );
}
